import { useTheme } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcRequestErrorReport } from "components";
import { FeedTopicHeaderResponse } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useWindowSize } from "hooks/useWindowSize";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { ComplexHeaderLoading } from "views/knowledge/components/ComplexInfoHeader";
import { FeedTopicHome } from "views/knowledge/feed/FeedTopic/FeedTopicHome";
import { TopicHeader } from "views/knowledge/feed/FeedTopic/TopicHeader";
import { TopicTrainings } from "views/lms/topic";

////////////////////////////////////////////////////////////////////////////////

type FeedTopicContextType = {
    isHome: boolean;
    topic: FeedTopicHeaderResponse;
    includeDescendants: boolean;
    openRightPanel: boolean;
    setOpenRightPanel: (value: boolean) => void;
    mode: FeedTopicMode;
    setMode: (value: FeedTopicMode) => void;
};

export type FeedTopicMode = "knowledge" | "lms";

const FeedTopicContext = React.createContext<FeedTopicContextType>(undefined!);

////////////////////////////////////////////////////////////////////////////////

export const useFeedTopicContext = () => React.useContext(FeedTopicContext);

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    isHome?: boolean;
    initialMode?: FeedTopicMode;
}

export const FeedTopic: React.FC<IProps> = ({ isHome = false, initialMode = "knowledge" }) => {
    const api = useCoreApi();
    const theme = useTheme();
    const navigate = useNavigate();

    const { id } = useParams();
    const { width } = useWindowSize({ wait: 50 });

    const [mode, setMode] = useState<FeedTopicMode>(initialMode);
    const [topicId, setTopicId] = useState<string>(id!);
    const [topic, setTopic] = useState<FeedTopicHeaderResponse | null>(null);
    const [includeDescendants, setIncludeDescendants] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null);
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);

    ///
    const getTopicHeader = async () => {
        setTopic(null);
        setViewError(null);

        try {
            var response = await api.v1.knowledgeFeed.getFeedTopicHeader({ topicId });
            setTopic(response.data);
        }
        catch (error) {
            setViewError(error);
        }
    };

    ///
    const handleReset = (newTopic?: FeedTopicHeaderResponse) => {
        if (newTopic) {
            setTopic(newTopic);
        }
        else if (topicId) {
            getTopicHeader();
        }
    };

    ///
    useEffect(() => {
        if (topicId) getTopicHeader();
    }, [topicId]);

    ///
    useDidUpdateEffect(() => {
        if (id && topic?.topicId != id) setTopicId(id);
    }, [id]);

    ///
    const showRightPanel = useMemo(() => {
        return width < theme.breakpoints.values["lg"];
    }, [width]);

    return (
        <FeedTopicContext.Provider
            value={
                {
                    topic: topic!,
                    isHome,
                    includeDescendants,
                    openRightPanel, setOpenRightPanel,
                    mode, setMode
                }
            }
        >
            {
                viewError
                    ? <MprcRequestErrorReport
                        error={viewError}
                        onRetry={() => getTopicHeader()}
                    />
                    : topic == null
                        ? <ComplexHeaderLoading defaultThumbnail={AppImages.TopicThumbnail} />
                        : (
                            <>
                                <TopicHeader
                                    onDescendants={setIncludeDescendants}
                                    onReset={handleReset}
                                    onTopicChange={
                                        (topicId) => mode == "knowledge"
                                            ? navigate(KnowledgePaths.FeedTopic(topicId))
                                            : navigate(KnowledgePaths.FeedTopicTrainings(topicId))
                                    }
                                    onRigthPanelButtonClick={
                                        mode == "knowledge" && showRightPanel
                                            ? () => setOpenRightPanel(state => !state)
                                            : undefined
                                    }
                                />

                                {mode == "knowledge"
                                    ? <FeedTopicHome />
                                    : <TopicTrainings />
                                }
                            </>
                        )
            }
        </FeedTopicContext.Provider>
    );
};
